import type {
  ComponentPropsWithoutRef,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  ReactNode,
} from "react";
import {
  useNavigate,
  type NavigateOptions,
  type To,
} from "react-router-dom";

type SurfaceCardTone = "default" | "muted" | "interactive";

type SurfaceCardElement = "div" | "section" | "article";

type SurfaceCardLinkConfig = {
  to?: To;
  state?: NavigateOptions["state"];
  replace?: NavigateOptions["replace"];
  preventScrollReset?: NavigateOptions["preventScrollReset"];
  relative?: NavigateOptions["relative"];
  label?: string;
  disabled?: boolean;
  onNavigate?: (
    event: ReactMouseEvent<HTMLDivElement> | ReactKeyboardEvent<HTMLDivElement>,
  ) => void;
};

type SurfaceCardProps = ComponentPropsWithoutRef<"div"> & {
  as?: SurfaceCardElement;
  tone?: SurfaceCardTone;
  link?: SurfaceCardLinkConfig;
};

type SurfaceCardHeaderProps = Omit<
  ComponentPropsWithoutRef<"div">,
  "title"
> & {
  title?: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  headingProps?: ComponentPropsWithoutRef<"div">;
};

type SurfaceCardSectionProps = ComponentPropsWithoutRef<"div">;

type SurfaceCardBodyProps = SurfaceCardSectionProps & {
  gap?: "none" | "tight" | "base" | "relaxed" | "spacious";
  textTone?: "inherit" | "muted" | "default" | "strong";
};

type SurfaceCardFooterProps = SurfaceCardSectionProps & {
  gap?: "tight" | "base" | "relaxed";
  justify?: "start" | "center" | "end" | "between";
  divider?: boolean;
  fullWidth?: boolean;
};

const cx = (...classes: Array<string | undefined | false>) =>
  classes.filter(Boolean).join(" ");

const baseCardClasses =
  "rounded-2xl border border-slate-200 bg-white p-6 shadow-xl dark:border-slate-800 dark:bg-slate-900";
const mutedToneClasses = "border-slate-100 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/50";
const interactiveToneClasses =
  "transition hover:border-slate-300 hover:bg-white dark:hover:border-slate-600 dark:hover:bg-slate-800";
const linkableClasses =
  "cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-500";
const headerClasses =
  "flex flex-wrap items-start justify-between gap-4";
const headingStackClasses = "flex min-w-0 flex-1 flex-col gap-1.5";
const titleClasses =
  "inline-flex flex-wrap items-center gap-2 text-xl font-semibold text-slate-900 dark:text-slate-100";
const subtitleClasses = "m-0 text-base text-slate-500 dark:text-slate-400";
const actionContainerClasses =
  "inline-flex flex-wrap items-center justify-end gap-4";
const bodyGapClasses: Record<
  NonNullable<SurfaceCardBodyProps["gap"]>,
  string
> = {
  none: "gap-0",
  tight: "gap-2",
  base: "gap-4",
  relaxed: "gap-6",
  spacious: "gap-8",
};
const bodyToneClasses: Record<
  NonNullable<SurfaceCardBodyProps["textTone"]>,
  string
> = {
  inherit: "",
  muted: "text-sm text-slate-600 dark:text-slate-400",
  default: "text-base text-slate-700 dark:text-slate-300",
  strong: "text-base text-slate-900 dark:text-slate-100",
};
const footerGapClasses: Record<
  NonNullable<SurfaceCardFooterProps["gap"]>,
  string
> = {
  tight: "gap-2",
  base: "gap-3",
  relaxed: "gap-4",
};
const footerJustifyClasses: Record<
  NonNullable<SurfaceCardFooterProps["justify"]>,
  string
> = {
  start: "justify-start",
  center: "justify-center",
  end: "justify-end",
  between: "justify-between",
};

const SurfaceCardHeader = ({
  title,
  subtitle,
  action,
  headingProps,
  className,
  children,
  ...rest
}: SurfaceCardHeaderProps) => {
  const subtitleContent =
    typeof subtitle === "string" ? (
      <p className={subtitleClasses}>{subtitle}</p>
    ) : (
      subtitle
    );

  return (
    <div className={cx(headerClasses, className)} {...rest}>
      <div
        {...headingProps}
        className={cx(headingStackClasses, headingProps?.className)}
      >
        {title && <div className={titleClasses}>{title}</div>}
        {subtitle ? subtitleContent : null}
        {children}
      </div>
      {action ? (
        <div className={actionContainerClasses}>{action}</div>
      ) : null}
    </div>
  );
};

const SurfaceCardBody = ({
  className,
  gap = "relaxed",
  textTone = "inherit",
  ...rest
}: SurfaceCardBodyProps) => {
  return (
    <div
      className={cx(
        "flex flex-col",
        bodyGapClasses[gap],
        bodyToneClasses[textTone],
        className,
      )}
      {...rest}
    />
  );
};

const SurfaceCardFooter = ({
  className,
  gap = "base",
  justify = "between",
  divider = false,
  fullWidth = false,
  ...rest
}: SurfaceCardFooterProps) => {
  return (
    <div
      className={cx(
        "mt-4 flex flex-wrap items-center text-sm text-slate-600 dark:text-slate-400",
        footerGapClasses[gap],
        footerJustifyClasses[justify],
        divider
          ? "border-t border-slate-100 pt-4 dark:border-slate-800"
          : "pt-2",
        fullWidth ? "w-full" : "",
        className,
      )}
      {...rest}
    />
  );
};

const BaseSurfaceCard = ({
  as: Component = "article",
  tone = "default",
  className,
  children,
  link,
  onClick,
  onKeyDown,
  tabIndex,
  role,
  ...rest
}: SurfaceCardProps) => {
  const navigate = useNavigate();

  const toneClass =
    tone === "muted"
      ? mutedToneClasses
      : tone === "interactive"
        ? interactiveToneClasses
        : "";
  const isLinkEnabled = Boolean(link && !link.disabled);

  const { ["aria-label"]: ariaLabelFromProps, ...restProps } = rest as typeof rest & {
    "aria-label"?: string;
  };
  const ariaLabel = link?.label ?? ariaLabelFromProps;

  const activateLink = (
    event:
      | ReactMouseEvent<HTMLDivElement>
      | ReactKeyboardEvent<HTMLDivElement>,
  ) => {
    if (!isLinkEnabled) {
      return;
    }
    link?.onNavigate?.(event);
    if (event.defaultPrevented) {
      return;
    }
    if (link?.to != null) {
      navigate(link.to, {
        replace: link.replace,
        state: link.state,
        preventScrollReset: link.preventScrollReset,
        relative: link.relative,
      });
    }
  };

  const handleClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    onClick?.(event);
    if (event.defaultPrevented) {
      return;
    }
    activateLink(event);
  };

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented) {
      return;
    }
    if (isLinkEnabled && (event.key === "Enter" || event.key === " ")) {
      event.preventDefault();
      activateLink(event);
    }
  };

  return (
    <Component
      className={cx(
        baseCardClasses,
        toneClass,
        isLinkEnabled ? linkableClasses : "",
        className,
      )}
      role={isLinkEnabled ? role ?? "link" : role}
      tabIndex={isLinkEnabled ? tabIndex ?? 0 : tabIndex}
      aria-label={ariaLabel}
      onClick={isLinkEnabled || onClick ? handleClick : onClick}
      onKeyDown={isLinkEnabled || onKeyDown ? handleKeyDown : onKeyDown}
      {...restProps}
    >
      {children}
    </Component>
  );
};

type SurfaceCardComponent = typeof BaseSurfaceCard & {
  Header: typeof SurfaceCardHeader;
  Body: typeof SurfaceCardBody;
  Footer: typeof SurfaceCardFooter;
};

export const SurfaceCard = Object.assign(BaseSurfaceCard, {
  Header: SurfaceCardHeader,
  Body: SurfaceCardBody,
  Footer: SurfaceCardFooter,
}) as SurfaceCardComponent;
